import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Menu, Monitor, Moon, Sun, User as UserIcon, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Theme, useTheme } from '../hooks/useTheme';
import EmitenteSelector from './EmitenteSelector';
import InstallPwaButton from './InstallPwaButton';

function themeLabel(t: Theme): string {
  if (t === 'light') return 'Tema claro';
  if (t === 'dark') return 'Tema escuro';
  return 'Tema do sistema';
}

function nextTheme(t: Theme): Theme {
  if (t === 'light') return 'dark';
  if (t === 'dark') return 'system';
  return 'light';
}

export default function AppBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const Icon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;
  const label = themeLabel(theme);

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 pt-safe backdrop-blur-xl dark:border-slate-800/80 dark:bg-[#080c14]/90">
      <div className="h-px bg-gradient-to-r from-transparent via-emerald-500/60 to-transparent" />
      <div className="flex h-14 items-center gap-2 px-3 md:gap-4 md:px-6">
        <Link to="/" className="group flex shrink-0 items-center gap-2" title="Início">
          <div className="grid h-8 w-8 place-items-center rounded-lg border border-emerald-300/30 bg-emerald-500 shadow-[0_10px_22px_-14px_rgba(16,185,129,0.9)]">
            <span className="h-display text-lg font-bold leading-none text-slate-950">7</span>
          </div>
          <div className="hidden flex-col leading-tight md:flex">
            <span className="h-display text-sm font-semibold text-slate-950 dark:text-slate-100">EmissorFiscal</span>
            <span className="-mt-0.5 text-[9px] uppercase tracking-[0.18em] text-slate-500">7notes / fiscal</span>
          </div>
        </Link>

        <div className="hidden h-6 w-px bg-slate-200 dark:bg-slate-800 sm:block" aria-hidden="true" />

        <div className="min-w-0 flex-1 md:max-w-lg">
          <EmitenteSelector />
        </div>

        <InstallPwaButton />

        <button
          type="button"
          onClick={() => setTheme(nextTheme(theme))}
          title={`${label} (clique para trocar)`}
          aria-label={label}
          className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-400 dark:hover:bg-slate-800/80 dark:hover:text-slate-100"
        >
          <Icon className="h-4 w-4" />
        </button>

        <div className="relative shrink-0">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-400 dark:hover:bg-slate-800/80 dark:hover:text-slate-100"
            aria-label="Menu do usuário"
          >
            {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>

          {menuOpen && (
            <div className="card-surface absolute right-0 top-full z-50 mt-1 w-60 py-1 shadow-xl">
              <div className="border-b border-slate-200 px-3 py-2.5 dark:border-slate-800">
                <div className="flex items-center gap-2 text-sm">
                  <div className="grid h-7 w-7 place-items-center rounded-full border border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/20">
                    <UserIcon className="h-3.5 w-3.5 text-emerald-700 dark:text-emerald-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-slate-950 dark:text-slate-100">{user?.nome_completo || user?.usuario}</div>
                    {user?.email && <div className="truncate text-xs text-slate-600 dark:text-slate-400">{user.email}</div>}
                  </div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-slate-800/70"
              >
                <LogOut className="h-4 w-4" />
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
